import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import { brand } from "../../config/theme";
import { useUserStore } from "../../store/userStore";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";

import { Header } from "../../components/base/Header";
import { AppsSidebar, useFilteredCategories } from "../../components/base/AppsSidebar";
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";
import CompanySelector from "../../components/base/CompanySelector";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";

import { useDashboardCounts } from "../../features/dashboard/useDashboardCounts";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useIsReseller } from "../../hooks/useIsReseller";
import { useVehiclesSubscription } from "../../hooks/useVehiclesSubscription";
import { useCentinelaSocket } from "../../features/centinela/useCentinelaSocket";
import { startCentinelaVisit } from "./components/bootVisit";
import UnsavedConfigModal from "./components/UnsavedConfigModal";
import { useCentinelaConfigGuard } from "./hooks/useCentinelaConfigGuard";
import { MENU, MONITOREO_IA_BASE, monitoreoIaApp } from "./config";
import { useAccess } from "../../hooks/useAccess";

export default function MonitoreoIaLayout() {
  const { t } = useTranslation("common");
  const { canAccessSectionOfApp } = useAccess();
  const { themeMode, setTheme } = useTheme();
  const filteredCategories = useFilteredCategories();
  const user = useUserStore((state) => state.user);
  const hasRedirected = useRef(false);
  const visitStarted = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  if (!visitStarted.current) {
    visitStarted.current = true;
    startCentinelaVisit();
  }

  const { isExpiringSoon, timeRemainingFormatted, isExpired, isAuthenticated, isInitialized } =
    useSessionCheck({ warningThreshold: 60 * 60 * 1000, autoRefresh: true });

  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);

  const handleAppSelect = (app: AppItem) => {
    if (app.isComingSoon) {
      Swal.fire({
        title: t("appsSidebar.comingSoon"),
        text: t("appsSidebar.comingSoonText", { name: app.name }),
        icon: "info",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (app.isLocked) {
      Swal.fire({
        title: t("appsSidebar.premiumFeature"),
        text: t("appsSidebar.premiumRequired", { name: app.name }),
        icon: "warning",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (app.route) {
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank");
    }
  };

  const companyId = useCompaniesStore((state) => state.companyId);
  const setCompanyId = useCompaniesStore((state) => state.setCompanyId);
  const allCompanies = useCompaniesStore((state) => state.allCompanies);
  const fetchAllCompanies = useCompaniesStore((state) => state.fetchAllCompanies);
  const { isReseller } = useIsReseller();

  useVehiclesSubscription();
  useCentinelaSocket();

  const { data: counts, isLoading: countsLoading } = useDashboardCounts(companyId);

  useEffect(() => {
    const storedCompanyId = localStorage.getItem("companyId");
    if (storedCompanyId && storedCompanyId !== companyId) setCompanyId(storedCompanyId);
    fetchAllCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasConfigChanges = useCentinelaConfigGuard((state) => state.hasChanges);
  const saveConfig = useCentinelaConfigGuard((state) => state.save);
  const discardConfig = useCentinelaConfigGuard((state) => state.discard);
  const [pendingPath, setPendingPath] = useState<string | null>(null);

  const requestNavigate = (path: string) => {
    if (!hasConfigChanges || path === location.pathname) {
      navigate(path);
      return;
    }
    setPendingPath(path);
  };

  const leavePendingPath = () => {
    if (!pendingPath) return;
    navigate(pendingPath);
    setPendingPath(null);
  };

  const MENU_COUNTS: Record<string, { count?: number; loading: boolean }> = {
    unidades: { count: counts?.units, loading: countsLoading },
    geocercas: { count: counts?.geofences, loading: countsLoading },
    rutas: { count: counts?.routes, loading: countsLoading },
    viajes: { count: counts?.trips, loading: countsLoading },
  };

  const menuItems = MENU
    .filter((item) => !item.hiddenFromMenu)
    .filter((item) => canAccessSectionOfApp("monitoreo-ia", item.id))
    .map((item) => ({
    id: item.id,
    label: t(item.labelKey),
    icon: item.icon,
    path: `${MONITOREO_IA_BASE}/${item.id}`,
    count: MENU_COUNTS[item.id]?.count,
    loading: MENU_COUNTS[item.id]?.loading,
  }));

  useEffect(() => {
    if (isInitialized && (isExpired || !isAuthenticated) && !hasRedirected.current) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user && location.pathname !== "/splash") {
      localStorage.setItem("lastRoute", location.pathname);
      navigate("/splash");
    }
  }, [user, location.pathname, navigate]);

  return (
    <div className="flex h-[100dvh] pt-safe-top flex-col bg-app">
      {isExpiringSoon && (
        <div className="flex items-center justify-center gap-2 bg-warning px-4 py-2 text-center text-sm text-white">
          <i className="ri-alarm-warning-line" />
          <span>{t("sessionBanner.expiring", { time: timeRemainingFormatted })}</span>
        </div>
      )}

      <Header
        title={t("monitoreoIa.appName")}
        subtitle={t("monitoreoIa.appSubtitle")}
        onOpenAppsSidebar={() => setIsAppsSidebarOpen(true)}
        isAppsSidebarOpen={isAppsSidebarOpen}
        onToggleMobileMenu={toggleMobileMenu}
        isMobileMenuOpen={isMobileMenuOpen}
        isReseller={isReseller}
        companies={allCompanies}
        selectedCompany={companyId ?? null}
        onCompanyChange={(id) => setCompanyId(id ?? null)}
      />

      <div className="flex min-h-0 flex-1">
        <AppSidebarMenu
          menuItems={menuItems}
          isMobileMenuOpen={isMobileMenuOpen}
          onCloseMobileMenu={closeMobileMenu}
          onNavigate={requestNavigate}
          companySelector={
            isReseller ? (
              <CompanySelector
                value={companyId ?? null}
                onChange={(id) => setCompanyId(id ?? null)}
                isClearable
                companies={allCompanies.length > 0 ? allCompanies : undefined}
                className="w-full"
              />
            ) : undefined
          }
          isActiveRoute={(itemId) => location.pathname === `${MONITOREO_IA_BASE}/${itemId}`}
        />

        <div className="flex min-w-0 flex-1 justify-center overflow-hidden">
          <Outlet />
        </div>
      </div>

      {pendingPath ? (
        <UnsavedConfigModal
          onSaveAndExit={() => {
            saveConfig();
            leavePendingPath();
          }}
          onDiscard={() => {
            discardConfig();
            leavePendingPath();
          }}
          onCancel={() => setPendingPath(null)}
        />
      ) : null}

      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId={monitoreoIaApp.id}
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={
          user
            ? {
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                username: user.username,
              }
            : undefined
        }
        onProfileClick={() => {
          closeAppsSidebar();
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
